import React, { useState, useEffect, useRef } from "react";
import { getFilterOption } from "./filterOptions.jsx";
import OperatorSelector from "./OperatorSelector";
import StringInput from "./StringInput";
import TagInput from "./TagInput";
import OptinStatusInput from "./OptinStatusInput";
import DateInput from "./DateInput";
import NumberInput from "./NumberInput";
import CustomFieldSelector from "./CustomFieldSelector";
import FrequencyInput from "./FrequencyInput";
import CampaignSelector from "./CampaignSelector";

const FilterInput = ({
  filterType,
  initialOperator,
  initialValue = "",
  groupIndex,
  orIndex,
  onSave,
  onCancel,
  tagOptions = [],
  customFields = [],
  position = "left",
  buttonPosition = null,
  containerWidth = 800,
  initialCustomFieldName = null,
  initialTagId = null,
  initialFrequency = null,
  initialCampaignType = null,
  initialCampaignId = null,
}) => {
  const [operator, setOperator] = useState(initialOperator);

  // Set default value for date inputs
  const getDefaultValue = () => {
    if (
      (filterType === "createdAt" ||
        filterType === "optinAt" ||
        filterType === "optoutAt") &&
      !initialValue
    ) {
      // Set default based on operator type
      if (operator?.inputType === "date") {
        // Set today's date as default for date inputs
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      } else if (operator?.inputType === "number") {
        // Set default number of days (e.g., 30)
        return "30";
      }
    }
    return initialValue;
  };

  const [value, setValue] = useState(getDefaultValue());
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedCustomField, setSelectedCustomField] = useState(null);
  const [customFieldValue, setCustomFieldValue] = useState("");
  const [selectedFrequency, setSelectedFrequency] = useState(
    filterType === "receivedMessageAt" ||
      filterType === "placedOrderAt" ||
      filterType === "openedMessageAt" ||
      filterType === "clickedUrlAt" ||
      filterType === "clickedButtonAt"
      ? initialFrequency || "atLeastOnce"
      : "atLeastOnce",
  );
  const [selectedCampaignType, setSelectedCampaignType] = useState(
    filterType === "receivedMessageAt" ||
      filterType === "placedOrderAt" ||
      filterType === "openedMessageAt" ||
      filterType === "clickedUrlAt" ||
      filterType === "clickedButtonAt"
      ? initialCampaignType || "any"
      : "any",
  );
  const [campaignId, setCampaignId] = useState(
    filterType === "receivedMessageAt" ||
      filterType === "placedOrderAt" ||
      filterType === "openedMessageAt" ||
      filterType === "clickedUrlAt" ||
      filterType === "clickedButtonAt"
      ? initialCampaignId || ""
      : "",
  );
  const filterInputRef = useRef(null);

  const filterOption = getFilterOption(filterType);

  // Set default operator if none provided
  useEffect(() => {
    if (
      !operator &&
      filterOption &&
      filterOption.operators &&
      filterOption.operators.length > 0
    ) {
      setOperator(filterOption.operators[0]);
    }
  }, [operator, filterOption]);

  useEffect(() => {
    if (operator && operator.requiresValue !== undefined) {
      if (!operator.requiresValue) {
        setValue("");
      }
    }
  }, [operator]);

  // Initialize selectedCustomField when editing an existing custom field filter
  useEffect(() => {
    if (filterType === "customField" && customFields && !selectedCustomField) {
      // Convert customFields object to array format if needed
      let customFieldsArray = [];
      if (Array.isArray(customFields)) {
        customFieldsArray = customFields;
      } else if (typeof customFields === "object") {
        // Convert object like {cat_name: true, dog_name: true} to array format
        customFieldsArray = Object.keys(customFields).map((name) => ({
          name,
          id: name,
        }));
      }

      // When editing an existing custom field filter, we need to set selectedCustomField
      if (initialCustomFieldName) {
        const matchingCustomField = customFieldsArray.find(
          (field) => field.name === initialCustomFieldName,
        );
        if (matchingCustomField) {
          setSelectedCustomField(matchingCustomField);
          // Also set the custom field value from initialValue
          setCustomFieldValue(initialValue);
        }
      }
    }
  }, [
    filterType,
    initialCustomFieldName,
    initialValue,
    selectedCustomField,
    customFields,
  ]);

  // Initialize selectedTag when editing an existing tag filter
  useEffect(() => {
    if (filterType === "tag" && tagOptions && !selectedTag && initialTagId) {
      const matchingTag = tagOptions.find((tag) => tag.id === initialTagId);
      if (matchingTag) {
        setSelectedTag(matchingTag);
      }
    }
  }, [filterType, initialTagId, selectedTag, tagOptions]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterInputRef.current &&
        !filterInputRef.current.contains(event.target)
      ) {
        onCancel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onCancel]);

  const handleOperatorChange = (newOperator) => {
    setOperator(newOperator);
    if (!newOperator.requiresValue) {
      setValue("");
    } else {
      // Reset value when switching between different input types to avoid parsing errors
      if (newOperator.inputType !== operator?.inputType) {
        if (newOperator.inputType === "date") {
          // Set today's date as default for date inputs
          const today = new Date();
          const year = today.getFullYear();
          const month = String(today.getMonth() + 1).padStart(2, "0");
          const day = String(today.getDate()).padStart(2, "0");
          setValue(`${year}-${month}-${day}`);
        } else if (newOperator.inputType === "number") {
          // Set default number of days
          setValue("30");
        } else {
          setValue("");
        }
      }
      // For tag filters, preserve the selected tag when switching operators
      if (filterType === "tag" && selectedTag) {
        setValue(selectedTag.label);
      }
    }
  };

  const handleSave = () => {
    const filterData = {
      type: filterType,
      operator: operator.key,
      operatorLabel: operator.label,
      requiresValue: operator.requiresValue,
      value: operator.requiresValue
        ? filterType === "customField"
          ? customFieldValue
          : value
        : "",
      groupIndex,
      orIndex,
      ...(filterType === "tag" &&
        operator.requiresValue &&
        selectedTag && {
          tagId: selectedTag.id,
        }),
      ...(filterType === "customField" &&
        selectedCustomField && {
          customFieldId: selectedCustomField.id,
          customFieldName: selectedCustomField.name,
        }),
      ...((filterType === "receivedMessageAt" ||
        filterType === "placedOrderAt" ||
        filterType === "openedMessageAt" ||
        filterType === "clickedUrlAt" ||
        filterType === "clickedButtonAt") &&
        selectedFrequency && {
          frequency: selectedFrequency,
          frequencyLabel: filterOption.frequencyOptions.find(
            (opt) => opt.value === selectedFrequency,
          )?.label,
        }),
      ...((filterType === "receivedMessageAt" ||
        filterType === "placedOrderAt" ||
        filterType === "openedMessageAt" ||
        filterType === "clickedUrlAt" ||
        filterType === "clickedButtonAt") &&
        selectedCampaignType && {
          campaignType: selectedCampaignType,
          campaignTypeLabel: filterOption.campaignOptions.find(
            (opt) => opt.value === selectedCampaignType,
          )?.label,
          campaignId: selectedCampaignType !== "any" ? campaignId : null,
        }),
    };
    onSave(filterData);
  };

  const handleTagSelect = (tag) => {
    setSelectedTag(tag);
    setValue(tag.label);
    // Auto-save after tag selection
    setTimeout(() => {
      const filterData = {
        type: filterType,
        operator: operator.key,
        operatorLabel: operator.label,
        requiresValue: operator.requiresValue,
        value: tag.label,
        groupIndex,
        orIndex,
        tagId: tag.id,
      };
      onSave(filterData);
    }, 0);
  };

  const handleOptinStatusSelect = (optinStatusValue) => {
    setValue(optinStatusValue);
    // Auto-save after optin status selection
    setTimeout(() => {
      const filterData = {
        type: filterType,
        operator: operator.key,
        operatorLabel: operator.label,
        requiresValue: operator.requiresValue,
        value: optinStatusValue,
        groupIndex,
        orIndex,
      };
      onSave(filterData);
    }, 0);
  };

  const handleCustomFieldSelect = (customField) => {
    setSelectedCustomField(customField);
    setValue(customField.name);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  if (!filterOption || !operator) return null;

  // Calculate positioning based on button position with overflow detection
  const getPositionStyles = () => {
    if (buttonPosition) {
      // Use flexible width for FilterInput (can wrap to multiple rows)
      const minFilterWidth = 300; // Minimum width of the filter input
      const maxFilterWidth = 500; // Maximum width before wrapping
      const gap = 8;
      const inlineLeft = buttonPosition.x + buttonPosition.width + gap;

      // Check if FilterInput would overflow the container
      const wouldOverflow = inlineLeft + minFilterWidth > containerWidth;

      if (wouldOverflow) {
        // Position on new row, aligned to the right edge of the button
        const rightAlignedLeft =
          buttonPosition.x + buttonPosition.width - minFilterWidth;
        const clampedLeft = Math.max(8, rightAlignedLeft); // Don't go beyond left edge
        const availableWidth = containerWidth - clampedLeft - 8; // 8px margin
        return {
          position: "absolute",
          left: `${clampedLeft}px`,
          top: `${buttonPosition.y + buttonPosition.height + 8}px`, // 8px gap below button
          zIndex: 50,
          width: `${Math.min(availableWidth, maxFilterWidth)}px`,
        };
      } else {
        // Position inline after the button, with flexible offset based on button position
        const topOffset = buttonPosition.y > 0 ? buttonPosition.y - 12 : -24;
        const availableWidth = containerWidth - inlineLeft - 8; // 8px margin
        return {
          position: "absolute",
          left: `${inlineLeft}px`,
          top: `${topOffset}px`,
          zIndex: 50,
          width: `${Math.min(availableWidth, maxFilterWidth)}px`,
        };
      }
    }

    // Fallback to original positioning logic
    return {
      position: "absolute",
      top: "0px",
      left: position === "right" ? "100%" : "0px",
      marginLeft: position === "right" ? "8px" : "0px",
      zIndex: 50,
    };
  };

  return (
    <div
      ref={filterInputRef}
      className="relative bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-lg"
      style={{
        ...getPositionStyles(),
      }}
    >
      {/* Done button positioned absolutely in top right */}
      <button
        onClick={handleSave}
        className="absolute top-2 right-2 px-3 py-1 text-sm font-medium text-pink rounded hover:bg-pink-50 focus:outline-none focus:ring-1 focus:ring-pink"
      >
        Done
      </button>

      {/* Main content area with padding for Done button */}
      <div
        className={`flex flex-wrap items-center gap-2 pr-16 ${operator.requiresValue && operator.inputType !== "number" && (operator.inputType === "date" || (filterType === "customField" && selectedCustomField)) ? "mb-2" : ""}`}
      >
        <span className="text-sm font-medium text-gray-700">
          {filterOption.label}
        </span>

        {/* Custom field selector in top row - always visible as dropdown */}
        {filterType === "customField" && (
          <CustomFieldSelector
            value={selectedCustomField ? selectedCustomField.name : ""}
            onChange={setValue}
            placeholder="Select custom field"
            autoFocus
            onSelect={handleCustomFieldSelect}
            customFields={customFields}
          />
        )}

        {/* Frequency selector for message filters */}
        {(filterType === "receivedMessageAt" ||
          filterType === "placedOrderAt" ||
          filterType === "openedMessageAt" ||
          filterType === "clickedUrlAt" ||
          filterType === "clickedButtonAt") && (
          <FrequencyInput
            value={selectedFrequency}
            onChange={(frequencyValue) => {
              setSelectedFrequency(frequencyValue);
            }}
            options={filterOption.frequencyOptions}
            onSelect={(frequencyValue) => {
              setSelectedFrequency(frequencyValue);
            }}
          />
        )}

        <OperatorSelector
          filterType={filterType}
          selectedOperator={operator.key}
          onOperatorChange={handleOperatorChange}
        />

        {/* Campaign selector for message filters - inline when no value input needed */}
        {(filterType === "receivedMessageAt" ||
          filterType === "placedOrderAt" ||
          filterType === "openedMessageAt" ||
          filterType === "clickedUrlAt" ||
          filterType === "clickedButtonAt") &&
          !operator.requiresValue && (
            <>
              <CampaignSelector
                value={selectedCampaignType}
                onChange={(campaignTypeValue) => {
                  setSelectedCampaignType(campaignTypeValue);
                }}
                options={filterOption.campaignOptions}
                onSelect={(campaignTypeValue) => {
                  setSelectedCampaignType(campaignTypeValue);
                }}
              />

              {/* Campaign ID input for specific campaign/automation */}
              {selectedCampaignType !== "any" && (
                <NumberInput
                  value={campaignId}
                  onChange={setCampaignId}
                  placeholder="Enter ID"
                  onKeyPress={handleSave}
                  autoFocus
                />
              )}
            </>
          )}

        {/* Inline number input for relative date operators */}
        {operator.inputType === "number" && operator.requiresValue && (
          <div className="inline-flex items-center gap-1">
            <NumberInput
              value={value}
              onChange={setValue}
              placeholder={operator.placeholder || filterOption.placeholder}
              autoFocus
              onKeyPress={handleSave}
            />
            <span className="text-sm text-gray-600">days ago</span>
          </div>
        )}

        {/* Inline dropdown inputs for tag and optin status */}
        {filterOption.inputType === "tag" && operator.requiresValue && (
          <TagInput
            value={value}
            onChange={setValue}
            onSelect={handleTagSelect}
            options={tagOptions}
            selected={selectedTag}
          />
        )}

        {filterOption.inputType === "optinStatus" && operator.requiresValue && (
          <OptinStatusInput
            value={value}
            onChange={setValue}
            placeholder={operator.placeholder || filterOption.placeholder}
            autoFocus
            onSelect={handleOptinStatusSelect}
          />
        )}

        {/* Inline string input for regular string fields (not custom fields) */}
        {filterType !== "customField" &&
          (operator.inputType === "string" ||
            filterOption.inputType === "string") &&
          operator.requiresValue && (
            <StringInput
              value={value}
              onChange={setValue}
              placeholder={operator.placeholder || filterOption.placeholder}
              onKeyPress={handleKeyPress}
              autoFocus
              filterType={filterType}
              maxLength={operator.maxLength}
            />
          )}
      </div>

      {/* Input section - only for non-number inputs */}
      {operator.requiresValue && operator.inputType !== "number" && (
        <div
          className={
            operator.inputType === "date"
              ? "w-full"
              : "inline-flex items-center gap-2"
          }
        >
          {filterType === "customField" && selectedCustomField ? (
            <StringInput
              value={customFieldValue}
              onChange={setCustomFieldValue}
              placeholder="Enter value"
              onKeyPress={handleKeyPress}
              maxLength={operator.maxLength}
            />
          ) : operator.inputType === "date" ? (
            <DateInput value={value} onChange={setValue} autoFocus />
          ) : null}
        </div>
      )}

      {/* Campaign selector for message filters - after date inputs when value input is needed */}
      {(filterType === "receivedMessageAt" ||
        filterType === "placedOrderAt" ||
        filterType === "openedMessageAt" ||
        filterType === "clickedUrlAt" ||
        filterType === "clickedButtonAt") &&
        operator.requiresValue && (
          <div className="flex items-center gap-2 mt-2">
            <CampaignSelector
              value={selectedCampaignType}
              onChange={(campaignTypeValue) => {
                setSelectedCampaignType(campaignTypeValue);
              }}
              options={filterOption.campaignOptions}
              onSelect={(campaignTypeValue) => {
                setSelectedCampaignType(campaignTypeValue);
              }}
            />

            {/* Campaign ID input for specific campaign/automation */}
            {selectedCampaignType !== "any" && (
              <NumberInput
                value={campaignId}
                onChange={setCampaignId}
                placeholder="Enter ID"
                onKeyPress={handleSave}
                autoFocus
              />
            )}
          </div>
        )}
    </div>
  );
};

export default FilterInput;
